import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import { StorageProcessState } from '../hooks/useStorage';

import { getFileType } from './files';

/** Supported media file extensions */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];

/**
 * Attachment/media type identifiers.
 *
 * Note on IMAGE vs PHOTO:
 * - Use `IMAGE` for internal components (AttachmentSlider, MessagePreview, etc.)
 * - Use `PHOTO` for react-chat-elements library (leads chat) which expects 'photo'
 *
 * @readonly
 * @enum {string}
 */
const AttachmentType = {
  AUDIO: 'audio',
  FILE: 'file',
  IMAGE: 'image',
  VIDEO: 'video',
  // Alias for react-chat-elements library compatibility (expects 'photo' not 'image')
  PHOTO: 'photo',
};

/**
 * Determines the media type of a file based on its URL extension.
 *
 * Parses the URL to extract the file extension and matches it against
 * known image, video, and audio extensions. Returns 'file' as fallback
 * for unrecognized or invalid URLs.
 *
 * @param {string} url - The URL of the media file to analyze.
 * @returns {string} The detected media type: 'photo', 'video', 'audio', or 'file'.
 */
const getMediaTypeFromUrl = (url) => {
  try {
    const ext = new URL(url).pathname.split('.').pop()?.toLowerCase() || '';
    if (IMAGE_EXTENSIONS.includes(ext)) return AttachmentType.PHOTO;
    if (VIDEO_EXTENSIONS.includes(ext)) return AttachmentType.VIDEO;
    if (AUDIO_EXTENSIONS.includes(ext)) return AttachmentType.AUDIO;
  } catch {
    // Invalid URL - fall through to return default
  }
  return AttachmentType.FILE;
};

/**
 * Constructs a media data object suitable for rendering in a chat message component.
 *
 * Generates different data structures based on the media type:
 * - **video**: Includes URI, videoURL, and download status for video playback.
 * - **audio**: Includes audioURL and audioType (defaults to 'audio/mp4') for audio playback.
 * - **photo/file**: Includes URI and download status for image display or file download.
 *
 * @param {string} attachmentUrl - The URL of the media attachment.
 * @param {string} mediaType - The type of media ('video', 'audio', 'photo', or 'file').
 * @returns {Object} A data object formatted for the chat message component.
 *
 */
const getMediaData = (attachmentUrl, mediaType) => {
  if (mediaType === AttachmentType.VIDEO) {
    return { uri: attachmentUrl, videoURL: attachmentUrl, status: { download: true } };
  }
  if (mediaType === AttachmentType.AUDIO) {
    return { audioURL: attachmentUrl, audioType: 'audio/mp4' };
  }
  return { uri: attachmentUrl, status: { download: true } };
};

const getProcessedAttachmentData = ({
  fileName,
  contentType,
  pathRef,
  fileSize,
}) => {
  const fileType = getFileType(contentType); // image, video, audio, etc...
  return {
    type: fileType,
    title: fileName,
    assetPath: pathRef,
    fileSize,
    mimeType: contentType,
  };
};

const attachmentShape = PropTypes.shape({
  type: PropTypes.oneOf(Object.values(AttachmentType)),
  fileSize: PropTypes.number,
  assetPath: PropTypes.string,
  title: PropTypes.string,
  mimeType: PropTypes.string,
});

const attachmentPreviewPropTypes = {
  attachmentData: attachmentShape,
  assetLocalUrl: PropTypes.string.isRequired,
};

/**
 * Uploads attachments to firebase storage and returns the references
 *
 * @param {File[]} fileAttachments - Array of files to upload
 * @param {File} audioAttachment - Audio file to upload
 * @param {string} userId - User ID of who is uploading the attachments
 * @param {Function} uploadAttachments - Function to upload attachments
 * @param {Function} deleteAttachments - Function to delete attachments
 * @param {string} storagePath - Storage path to upload attachments to
 * @returns {Promise<{attachmentsRefs: string[], attachmentsData: Object[], storageTaskFailed: boolean}>}
 */
const uploadMessageAttachments = async ({
  fileAttachments,
  audioAttachment,
  userId,
  uploadAttachments,
  deleteAttachments,
  storagePath,
}) => {
  const attachmentsRefs = [];
  const attachmentsData = [];

  // If this function is called there is at least one file attachment or an audio attachment
  const allAttachments = audioAttachment ? [...fileAttachments, audioAttachment] : fileAttachments;

  const filesToAdd = allAttachments.map((file) => {
    const lastDotIndex = file.name.lastIndexOf('.');
    return {
      // This is then revoked once the upload finishes (correctly or not)
      dataUrl: URL.createObjectURL(file),
      format: file.name.slice(lastDotIndex + 1),
      fileName: file.name.slice(0, lastDotIndex),
      fileType: file.type,
    };
  });

  try {
    const result = await uploadAttachments(filesToAdd, storagePath);

    let storageTaskFailed = false;
    result.forEach((res) => {
      if (res.state === StorageProcessState.SUCCESS) {
        const {
          name,
          contentType,
          size,
        } = res.metadata;
        attachmentsRefs.push(res.fileRef);
        attachmentsData.push(getProcessedAttachmentData({
          fileName: name,
          pathRef: res.fileRef,
          contentType,
          fileSize: size,
        }));
      } else {
        storageTaskFailed = true;
      }
    });
    if (storageTaskFailed) {
      // If some upload fails, we should stop the sending process
      // and delete the files that were uploaded (if there is any)
      if (attachmentsRefs.length) {
        const filesToDelete = attachmentsRefs.map((fileRef) => ({ storagePath: fileRef }));
        await deleteAttachments(filesToDelete);
      }
    }
    return { attachmentsRefs, attachmentsData, storageTaskFailed };
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        userId,
      },
    });
    // Returning this to handle error messages from the caller function
    return { storageTaskFailed: true };
  }
};

/**
 * Maps file attachments to the format expected by uploadAttachments.
 * Supports both plain File objects and { file, dataUrl } objects.
 *
 * @param {Array<File|{file: File, dataUrl: string}>} fileAttachments - Array of File objects or file attachment objects
 * @returns {Array<{dataUrl: string, format: string, fileName: string, fileType: string}>}
 */
const mapFilesToUploadFormat = (fileAttachments) => fileAttachments.map((attachment) => {
  const file = attachment instanceof File ? attachment : attachment.file;
  const dataUrl = attachment instanceof File ? URL.createObjectURL(attachment) : attachment.dataUrl;

  const lastDotIndex = file.name.lastIndexOf('.');
  return {
    dataUrl,
    format: file.name.slice(lastDotIndex + 1),
    fileName: file.name.slice(0, lastDotIndex),
    fileType: file.type,
  };
});

/**
 * Processes upload results, separating successful and failed uploads.
 * If any uploads failed, cleans up successful uploads by deleting them.
 *
 * @param {Array} results - Results from uploadAttachments
 * @param {Function} deleteAttachments - Function to delete attachments
 * @returns {Promise<{uploadedRefs: Array, failed: boolean}>}
 */
const processUploadResults = async (results, deleteAttachments) => {
  const uploadedRefs = [];
  const failedFiles = [];

  results.forEach((res) => {
    if (res.state === StorageProcessState.SUCCESS) {
      uploadedRefs.push(res);
    } else {
      failedFiles.push(res);
    }
  });

  if (failedFiles.length > 0) {
    if (uploadedRefs.length > 0) {
      await deleteAttachments(uploadedRefs.map(({ fileRef }) => ({ storagePath: fileRef })));
    }
    return { uploadedRefs: [], failed: true };
  }

  return { uploadedRefs, failed: false };
};

/**
 * Cleans the attachment title from a appended UUID
 *
 * e.g. "image-2a372f0f-2a9f-4097-ac98-28ce737a1f09.jpg" -> "image.jpg"
 * @param {string} title - The title of the attachment
 * @returns {string} - The cleaned title
 */
const cleanAttachmentTitle = (title) => {
  const uuidRegex = /-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const lastDotIndex = title.lastIndexOf('.');
  const fullName = title.substring(0, lastDotIndex);
  const extension = title.substring(lastDotIndex + 1);
  return `${fullName.replace(uuidRegex, '')}.${extension}`;
};

export {
  AttachmentType,
  getMediaTypeFromUrl,
  getMediaData,
  getProcessedAttachmentData,
  attachmentShape,
  attachmentPreviewPropTypes,
  uploadMessageAttachments,
  mapFilesToUploadFormat,
  processUploadResults,
  cleanAttachmentTitle,
};
